import { useCallback, useEffect, type MutableRefObject, type RefObject } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useDangerousQueryGate } from './useDangerousQueryGate';
import { useAiExecutionRunner } from './useAiExecutionRunner';
import { toast } from '../../../../components/ui/toaster';
import type { QueryExecuteSource, QueryExecutionOutcome } from './useQueryExecution';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { QueryEditorHandle } from '../components/QueryEditor';
import { readLiveQuery } from '../components/liveQuery';
import { useAnalytics } from '../../../../core/analytics';
import type { VisualStatementRun } from '../../visual-query-editor/execute/visualStatementRun';
import { useVqeStore } from '../../visual-query-editor/store/vqeStore';
import { releaseTabSubmission, tryClaimTabSubmission } from '../services/tabSubmissionGate';

type ExecuteOptions = { includeActualPlan?: boolean; source?: QueryExecuteSource };

type UseExecutionActionsParams = {
  tabId: string;
  engineId: DatabaseEngineId;
  connectionId: string | undefined;
  /** The tab's live database, read at execution time for the agent target binding. */
  tabDatabase: string | undefined;
  /** The tab's persisted query text (auto-execute-on-mount reads it). */
  storeQuery: string | undefined;
  autoExecuteOnMount: boolean | undefined;
  /** Debounced React query state (MCP execution fallback read). */
  query: string;
  formatOnExecute: boolean;
  editorRef: RefObject<QueryEditorHandle | null>;
  queryRef: MutableRefObject<string>;
  flushQueryDebounced: { flush: () => void };
  prepareForExecution: (queryText: string) => Promise<boolean>;
  executeCurrentQuery: (queryText: string, options?: ExecuteOptions) => Promise<QueryExecutionOutcome>;
  /** Records whether an execution ran a subset of the buffer (stays in the host — the peek path calls it too). */
  trackExecutionScope: (queryText: string) => void;
  /**
   * The Visual view's shown statement, or null while the SQL view shows. When
   * non-null, Execute / F5 / Cmd/Ctrl+Enter / results refresh / actual plan
   * run THIS statement — never a hidden editor selection or caret statement.
   * Shift+F5 ("Execute whole buffer") still runs the whole script.
   */
  resolveVisualRun?: () => VisualStatementRun | null;
};

/**
 * Execution orchestration for a query editor tab: every path that runs SQL —
 * toolbar / keyboard / floating bars / MCP / auto-execute-on-mount — funnels
 * through the dangerous-query gate (`runQueryText`) into `doExecuteQuery`
 * (debounce flush + transaction auto-begin + execute), plus the source-tagged
 * execute wrappers. Behavior carried verbatim from the previous inline
 * implementation.
 */
export function useExecutionActions({
  tabId,
  engineId,
  connectionId,
  tabDatabase,
  storeQuery,
  autoExecuteOnMount,
  query,
  formatOnExecute,
  editorRef,
  queryRef,
  flushQueryDebounced,
  prepareForExecution,
  executeCurrentQuery,
  trackExecutionScope,
  resolveVisualRun,
}: UseExecutionActionsParams) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);
  const { track } = useAnalytics();

  // Actually execute the query (called directly or after confirmation)
  // In manual/smart mode, auto-begins a transaction before the first query (smart only for DML).
  const doExecuteQuery = useCallback(async (
    queryText: string,
    options?: ExecuteOptions,
  ): Promise<QueryExecutionOutcome> => {
    // Force-flush any pending keystroke debounce so the workspace store and
    // MCP editor snapshot reflect the buffer that was actually executed.
    // Without this, fast type-then-execute can leave the store up to 200ms
    // behind the executed SQL, which downstream readers (MCP context,
    // history, save dirty-check) would see as a mismatch.
    flushQueryDebounced.flush();
    // One submission at a time per tab, from here through the BEGIN to the
    // dispatch (`tabSubmissionGate.ts`): a second Execute while the tab's
    // BEGIN is on the wire is ignored instead of running its statement too.
    const claim = tryClaimTabSubmission(tabId);
    if (!claim) {
      return { success: false, errorMessage: 'A run is already starting on this tab.' };
    }
    let dispatched: Promise<QueryExecutionOutcome>;
    try {
      // Defense in depth: prepareForExecution must never take Execute down
      // silently. An unexpected throw (e.g. a future engine that isn't gated)
      // surfaces as a toast instead of an unhandled rejection.
      let canProceed = false;
      try {
        canProceed = await prepareForExecution(queryText);
      } catch (error) {
        toast.error({
          title: 'Failed to prepare execution',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
        return { success: false, errorMessage: 'Failed to prepare execution.' };
      }
      if (!canProceed) return { success: false, errorMessage: 'Execution was not started.' };
      trackExecutionScope(queryText);
      // Dispatched once this returns: `executeCurrentQuery` marks the tab
      // executing before its first await, and `isExecuting` takes over.
      dispatched = executeCurrentQuery(queryText, options);
    } finally {
      releaseTabSubmission(tabId, claim);
    }
    return await dispatched;
  }, [tabId, executeCurrentQuery, prepareForExecution, trackExecutionScope, flushQueryDebounced]);

  // Dangerous-query confirmation gate — owns the destructive-query dialog state
  // and the `runQueryText` entry point (risk gate + dialog) every execution
  // path funnels through.
  const {
    dangerInfo,
    dangerousDialogOpen,
    handleDangerousConfirm,
    handleDangerousCancel,
    runQueryText,
  } = useDangerousQueryGate({ engineId, doExecuteQuery });

  // Agent / MCP execution requests parked in `aiExecutionStore`. Declared after
  // `runQueryText` exists so an awaited request (the agent execute-with-confirm
  // protocol) is routed through the destructive-query gate — a session-scoped
  // "always" grant still re-prompts on DROP/DELETE/TRUNCATE-class statements.
  // The MCP path keeps its historic fire-and-forget behaviour.
  useAiExecutionRunner({
    tabId,
    connectionId,
    // Live target, checked against the one the user approved: a rebind between
    // approval and execution must refuse, not silently redirect the statement.
    database: tabDatabase,
    getTextToExecute: (selectedOnly) => {
      const selectedText = selectedOnly ? (editorRef.current?.getSelectedText() || '') : '';
      return selectedText.trim() ? selectedText : (editorRef.current?.getValue() || query);
    },
    startExecution: (textToExecute) => { void doExecuteQuery(textToExecute, { source: 'mcp' }); },
    // MUST stay `runQueryText` (not `doExecuteQuery`): that is what puts the
    // agent path through `evaluateExecutionGate` + the confirmation dialog, so
    // a destructive statement still asks even under an "Always this session"
    // grant — and the `ai-agent-request` source is what selects the stricter
    // agent calibration inside the gate. See `queryExecutionGate.test.ts`.
    // The `guard` is the runner's own target binding. It is checked once here,
    // before the gate, and again when the user confirms — the dialog stays open
    // for human-scale time and the tab can be rebound underneath it.
    runToCompletion: (textToExecute, guard) =>
      runQueryText(textToExecute, { source: 'ai-agent-request', guard }),
  });

  // Auto-execute on mount (used by "Select top 1000 rows" context menu and
  // by Schema Compare's "Open & Execute" split button).
  // Clears the flag BEFORE executing so a slow executeQueryV2 finishing
  // after a re-render can't cause a duplicate run. Runs through
  // `runQueryText` so the destructive-query confirmation dialog gates
  // execution for any pre-canned script that contains DROP/DELETE/TRUNCATE
  // (Schema Compare's sync scripts are the load-bearing case).
  // Transactions, history, analytics, breadcrumbs, and the loading
  // message all still fire normally because `runQueryText` defers to
  // `doExecuteQuery` once the danger check passes.
  useEffect(() => {
    if (!autoExecuteOnMount) return;
    if (!connectionId) return; // wait until the tab has a connection; effect re-runs when it appears
    const queryToRun = storeQuery?.trim();
    if (!queryToRun) {
      // Nothing to run — clear the flag so we don't loop on empty state.
      updateTabContent(tabId, { autoExecuteOnMount: undefined });
      return;
    }
    updateTabContent(tabId, { autoExecuteOnMount: undefined });
    runQueryText(queryToRun, { source: 'auto-execute-on-mount' });
  }, [autoExecuteOnMount, connectionId, storeQuery, tabId, updateTabContent, runQueryText]);

  // Run the Visual view's shown statement; false while the SQL view shows
  // (the caller then runs its own SQL-view scope).
  const runVisualStatement = useCallback((
    trigger: 'button' | 'shortcut' | 'run-on-change',
    includeActualPlan?: boolean,
  ): boolean => {
    const run = resolveVisualRun?.();
    if (!run) return false;
    track('feature:vqe_execute', { kind: run.kind, source: trigger });
    const source =
      trigger === 'button' ? 'vqe-execute-button' : trigger === 'shortcut' ? 'vqe-keyboard-shortcut' : 'vqe-run-on-change';
    void runQueryText(run.sql, {
      source,
      includeActualPlan,
      confirmWith: run.confirmWith,
    });
    return true;
  }, [resolveVisualRun, runQueryText, track]);

  const handleExecute = useCallback((source: QueryExecuteSource) => {
    // The toolbar/keyboard Execute always runs the full editor content.
    // To run just a selection, users click the floating Execute button that
    // appears next to their selection (see `handleExecuteSelection`).
    if (formatOnExecute) {
      editorRef.current?.formatSql();
    }

    const textToExecute = readLiveQuery(editorRef.current, queryRef.current, 'Execution cancelled');
    if (textToExecute === null) return;
    runQueryText(textToExecute, { source });
  }, [runQueryText, formatOnExecute, queryRef, editorRef]);

  // Separate wrapper so the toolbar button produces a stable, source-tagged callback.
  const handleExecuteFromToolbar = useCallback(() => {
    if (!runVisualStatement('button')) handleExecute('execute-button');
  }, [handleExecute, runVisualStatement]);

  // Separate wrapper for results grid re-execute to avoid creating new function on every render.
  const handleResultsGridReExecute = useCallback(() => {
    if (!runVisualStatement('button')) handleExecute('results-grid-re-execute');
  }, [handleExecute, runVisualStatement]);

  // Floating selection Execute button: runs only the selected SQL. Does NOT
  // format (formatting would reset the selection the user just acted on).
  // `source` distinguishes the trigger (inline bar click vs keyboard) so
  // the analytics event reflects where the partial-execution came from.
  const handleExecuteSelection = useCallback((
    selectedSql: string,
    source: QueryExecuteSource = 'inline-selection-execute',
  ) => {
    runQueryText(selectedSql, { source });
  }, [runQueryText]);

  // Stable wrappers for the two inline floating bars so analytics can tell
  // them apart (`inline-selection-execute` vs `inline-cursor-execute`).
  const handleExecuteFromSelectionBar = useCallback(
    (sql: string) => handleExecuteSelection(sql, 'inline-selection-execute'),
    [handleExecuteSelection],
  );
  const handleExecuteFromCursorBar = useCallback(
    (sql: string) => handleExecuteSelection(sql, 'inline-cursor-execute'),
    [handleExecuteSelection],
  );
  // Toolbar Execute button when the inline bar is OFF and the user has a
  // runnable selection — runs only the selection (legacy "Selection" mode).
  const handleExecuteFromToolbarSelection = useCallback(
    (sql: string) => handleExecuteSelection(sql, 'inline-selection-execute'),
    [handleExecuteSelection],
  );

  const handleExecuteWithActualPlan = useCallback(() => {
    if (runVisualStatement('button', true)) return;
    if (formatOnExecute) {
      editorRef.current?.formatSql();
    }

    const textToExecute = readLiveQuery(editorRef.current, queryRef.current, 'Execution cancelled');
    if (textToExecute === null) return;
    runQueryText(textToExecute, { includeActualPlan: true, source: 'execute-with-plan' });
  }, [runQueryText, formatOnExecute, queryRef, editorRef, runVisualStatement]);

  // Refresh the edited results after saving them: re-run the SQL that
  // produced them (it already passed the gate when it ran) — never the
  // buffer or statement shown NOW, which may have moved on to other SQL, a
  // DELETE included. Falls back to the live buffer before any recorded run.
  const handleReExecuteAfterSave = useCallback(() => {
    const executed = useVqeStore.getState().lastExecuted[tabId];
    const textToExecute = executed ?? readLiveQuery(editorRef.current, queryRef.current, 'Execution cancelled');
    if (textToExecute === null) return;
    if (textToExecute.trim()) {
      trackExecutionScope(textToExecute);
      executeCurrentQuery(textToExecute, { source: 'edit-save' });
    }
  }, [executeCurrentQuery, trackExecutionScope, queryRef, editorRef, tabId]);

  return {
    dangerInfo,
    dangerousDialogOpen,
    handleDangerousConfirm,
    handleDangerousCancel,
    handleExecute,
    handleExecuteFromToolbar,
    handleResultsGridReExecute,
    handleExecuteSelection,
    handleExecuteFromSelectionBar,
    handleExecuteFromCursorBar,
    handleExecuteFromToolbarSelection,
    handleExecuteWithActualPlan,
    handleReExecuteAfterSave,
    runVisualStatement,
  };
}
